/**
 * Admin user-management + roster hooks. Back the AdminAllUsers management
 * table, the Roster Health panel, and the Audit Log view.
 *
 * Endpoints (all admin-gated, prefix /admin):
 *   GET   /users/system            list every user
 *   PATCH /users/{id}              partial update (role/FA/active/test/visible)
 *   POST  /users/bulk              apply one change to many users
 *   GET   /roster                  canonical FA roster
 *   POST  /roster                  add a roster entry
 *   DELETE /roster/{id}            soft-remove a roster entry
 *   GET   /roster/health           reconcile councils vs roster
 *   GET   /audit-log               recent admin actions
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type {
  AdminUserCreate,
  AdminUserDetail,
  AdminUserUpdate,
  AuditLogRow,
  BulkUpdateResult,
  EmailLogResponse,
  MondayMailPreview,
  ReconcileResponse,
  RosterEntry,
  RosterEntryCreate,
  RosterHealth,
  SystemUser,
  UserActivityRow,
  UserApplicationRow,
} from '../types/admin'

interface EmailLogFilters {
  status?: string
  recipient?: string
  event?: string
  limit?: number
}

interface SystemUserFilters {
  role?: string
  includeTest?: boolean
}

export function useSystemUsers({ role, includeTest = true }: SystemUserFilters) {
  return useQuery<SystemUser[]>({
    queryKey: ['admin', 'users', 'system', { role: role || '', includeTest }],
    queryFn: async () => {
      const params: Record<string, string | boolean> = { include_test: includeTest }
      if (role) params.role = role
      const res = await api.get('/admin/users/system', { params })
      return res.data
    },
  })
}

/** Invalidate every admin-users query regardless of filter args. */
function invalidateUsers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'users', 'system'] })
  qc.invalidateQueries({ queryKey: ['admin', 'roster', 'health'] })
  qc.invalidateQueries({ queryKey: ['admin', 'audit-log'] })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, change }: { id: number; change: AdminUserUpdate }) => {
      const res = await api.patch(`/admin/users/${id}`, change)
      return res.data as SystemUser
    },
    onSuccess: () => invalidateUsers(qc),
  })
}

export function useBulkUpdateUsers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, change }: { ids: number[]; change: AdminUserUpdate }) => {
      const res = await api.post('/admin/users/bulk', { user_ids: ids, change })
      return res.data as BulkUpdateResult
    },
    onSuccess: () => invalidateUsers(qc),
  })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: AdminUserCreate) => {
      const res = await api.post('/admin/users', input)
      return res.data as SystemUser
    },
    onSuccess: () => invalidateUsers(qc),
  })
}

/** Full detail for one user (drawer). */
export function useUserDetail(id: number | null) {
  return useQuery<AdminUserDetail>({
    queryKey: ['admin', 'users', 'detail', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await api.get(`/admin/users/${id}`)
      return res.data
    },
  })
}

/** A user's applications (reuses GET /applications?applicant_id=). */
export function useUserApplications(id: number | null) {
  return useQuery<UserApplicationRow[]>({
    queryKey: ['admin', 'users', 'applications', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await api.get('/applications', { params: { applicant_id: id } })
      return res.data
    },
  })
}

/** A user's activity/notifications (GET /admin/activity?user_id=). */
export function useUserActivity(id: number | null) {
  return useQuery<UserActivityRow[]>({
    queryKey: ['admin', 'users', 'activity', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await api.get('/admin/activity', { params: { user_id: id, limit: 50 } })
      return res.data
    },
  })
}

/** Admin actions that targeted this user (GET /admin/audit-log?target_user_id=). */
export function useUserAuditLog(id: number | null) {
  return useQuery<AuditLogRow[]>({
    queryKey: ['admin', 'users', 'audit', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await api.get('/admin/audit-log', {
        params: { target_user_id: id, limit: 50 },
      })
      return res.data
    },
  })
}

/** Reconcile councils from the roster. dryRun previews; live commits. */
export function useReconcileFromRoster() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await api.post('/admin/reconcile-from-roster', null, {
        params: { dry_run: dryRun },
      })
      return res.data as ReconcileResponse
    },
    onSuccess: (_data, dryRun) => {
      if (!dryRun) invalidateUsers(qc)
    },
  })
}

export function useRoster() {
  return useQuery<RosterEntry[]>({
    queryKey: ['admin', 'roster'],
    queryFn: async () => {
      const res = await api.get('/admin/roster')
      return res.data
    },
  })
}

export function useAddRosterEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (entry: RosterEntryCreate) => {
      const res = await api.post('/admin/roster', entry)
      return res.data as RosterEntry
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roster'] })
      qc.invalidateQueries({ queryKey: ['admin', 'roster', 'health'] })
    },
  })
}

export function useRemoveRosterEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/admin/roster/${id}`)
      return id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roster'] })
      qc.invalidateQueries({ queryKey: ['admin', 'roster', 'health'] })
    },
  })
}

export function useRosterHealth() {
  return useQuery<RosterHealth>({
    queryKey: ['admin', 'roster', 'health'],
    queryFn: async () => {
      const res = await api.get('/admin/roster/health')
      return res.data
    },
  })
}

export function useAuditLog(limit = 100) {
  return useQuery<AuditLogRow[]>({
    queryKey: ['admin', 'audit-log', limit],
    queryFn: async () => {
      const res = await api.get('/admin/audit-log', { params: { limit } })
      return res.data
    },
  })
}

/** Email outbox log (GET /admin/email-log) — every queued/sent/failed email. */
export function useEmailLog({ status, recipient, event, limit = 200 }: EmailLogFilters) {
  return useQuery<EmailLogResponse>({
    queryKey: ['admin', 'email-log', { status: status || '', recipient: recipient || '', event: event || '', limit }],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit }
      if (status) params.status = status
      if (recipient) params.recipient = recipient
      if (event) params.event = event
      const res = await api.get('/admin/email-log', { params })
      return res.data
    },
  })
}

export interface BroadcastFunnelRow {
  demand_id: number
  slug: string | null
  title: string | null
  focus_area_code: string | null
  event: string
  first_sent: string | null
  queued: number
  sent: number
  failed: number
  arrived: number
  applied: number
}

export interface BroadcastFunnelResponse {
  days: number
  items: BroadcastFunnelRow[]
  note: string
}

export function useBroadcastFunnel(days = 90) {
  return useQuery<BroadcastFunnelResponse>({
    queryKey: ['admin', 'broadcast-funnel', days],
    queryFn: async () => {
      const res = await api.get('/admin/broadcast-funnel', { params: { days } })
      return res.data
    },
  })
}

/** Re-queue one already-composed email (POST /admin/email-log/{id}/resend).
 *
 *  Sends the STORED body, so the recipient gets the message their colleagues
 *  got rather than a fresh render of a demand that has moved on. Until this
 *  existed, the only manual send in the app was the admin test email. */
export function useResendEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/admin/email-log/${id}/resend`)
      return res.data as { id: number; status: string; requeued: boolean; detail?: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'email-log'] })
    },
  })
}

/** What the app sends, when, and to whom (GET /admin/notification-catalog).
 *  Read-only by design — it explains delivery, it does not configure it. */
export interface NotificationCatalogEntry {
  key: string
  trigger: string
  audience: string
  emitted: boolean
  /** "Can I stop receiving this?" — the question straight after "why did I get it". */
  opt_out: string
  note?: string | null
}
/** The coming Monday mail, built by the sending code and not sent (#1500). */
export function useMondayMail(includeOff: boolean) {
  return useQuery<MondayMailPreview>({
    queryKey: ['admin', 'monday-mail', includeOff],
    queryFn: async () => {
      const res = await api.get('/admin/monday-mail', {
        params: includeOff ? { include_off: true } : undefined,
      })
      return res.data
    },
  })
}

export function useNotificationCatalog() {
  return useQuery<{ groups: Record<string, NotificationCatalogEntry[]> }>({
    queryKey: ['admin', 'notification-catalog'],
    queryFn: async () => {
      const res = await api.get('/admin/notification-catalog')
      return res.data
    },
  })
}

export interface ServiceAccountStatus {
  account: string
  connected: boolean
  configured: boolean
}

/** Email-sending service-account connection state (GET /admin/service-account/status). */
export function useServiceAccountStatus() {
  return useQuery<ServiceAccountStatus>({
    queryKey: ['admin', 'service-account-status'],
    queryFn: async () => (await api.get('/admin/service-account/status')).data as ServiceAccountStatus,
  })
}
